import React from 'react';
import { Group, Circle } from 'react-konva';
import { CentrePoleItem as CentrePoleItemType, Position, Size } from '../../types';

interface CentrePoleItemProps {
  id: string;
  item: CentrePoleItemType;
  position: Position;
  size: Size;
  rotation: number;
  isSelected: boolean;
  onDragStart: (e: any) => void;
  onDragEnd: (e: any) => void;
  onClick: (e: any) => void;
  onRotate: (id: string, rotation: number) => void;
}

const CentrePoleItem: React.FC<CentrePoleItemProps> = ({
  id,
  position,
  size,
  rotation,
  isSelected,
  onDragStart,
  onDragEnd,
  onClick,
  onRotate,
  item
}) => {
  // Handle rotation with the rotation handle
  const handleRotateStart = (e: any) => {
    e.cancelBubble = true; // Prevent parent drag
  };
  
  const handleRotateMove = (e: any) => {
    e.cancelBubble = true; // Prevent parent drag
    
    // Calculate the center of the item using the stage's coordinates
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    
    // Use getParent().getAbsolutePosition() to get the actual position
    // accounting for scale, rotation, and any transformations
    const groupNode = e.target.getParent();
    const groupAbsPos = groupNode.getAbsolutePosition();
    
    // Calculate angle between center of item and current mouse position
    const deltaX = pointerPos.x - groupAbsPos.x;
    const deltaY = pointerPos.y - groupAbsPos.y;
    
    // Calculate angle in degrees and add 90° to make rotation start from the top
    let angle = Math.atan2(deltaY, deltaX) * 180 / Math.PI + 90;
    
    // Normalize angle to 0-360 range
    if (angle < 0) angle += 360;
    
    // Round to nearest 15 degrees for better usability
    const snappedAngle = Math.round(angle / 15) * 15;
    
    onRotate(id, snappedAngle);
  };

  return (
    <Group
      id={id}
      x={position.x}
      y={position.y}
      rotation={rotation}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      {/* Centre Pole - a circle with crosshair pattern inside */}
      <Circle
        id={id}
        radius={size.width / 2}
        fill="#CCCCCC"
        stroke={isSelected ? "#3498db" : "#888"}
        strokeWidth={isSelected ? 2 : 1}
      />
      
      {/* Crosshair horizontal line */}
      <Circle
        id={id}
        radius={2}
        fill="#555"
        x={0}
        y={0}
      />
      
      {/* Rotation Handle - only show when selected */}
      {isSelected && (
        <>
          {/* Rotation indicator line */}
          <Circle
            x={0}
            y={-size.height / 2 - 20}
            radius={5}
            fill="#3498db"
            stroke="#fff"
            strokeWidth={1}
            draggable
            onDragStart={handleRotateStart}
            onDragMove={handleRotateMove}
            onMouseEnter={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'grab';
            }}
            onMouseLeave={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'default';
            }}
          />
        </>
      )}
    </Group>
  );
};

export default CentrePoleItem; 